import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, UserPlus } from "lucide-react";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { CATEGORIES } from "@/lib/categories";

const CATS = CATEGORIES;

export default function NewMission() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const chantierIdFromQuery = params.get("chantier_id");
  const inviteFromQuery = params.get("invite");
  const [form, setForm] = useState({
    title: "",
    category: CATS[0],
    description: "",
    budget_min: "",
    budget_max: "",
    city: "",
    surface_m2: "",
    address: "",
    deadline: null,
    mo_fee_type: "",
    mo_fee_value: "",
    mo_fee_vat_franchise: false,
    final_client_name: "",
    final_client_address: "",
    final_client_email: "",
  });
  const [artisans, setArtisans] = useState([]);
  const [invited, setInvited] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emailInvites, setEmailInvites] = useState([]);
  const [penaltyEnabled, setPenaltyEnabled] = useState(false);
  const [penaltyRate, setPenaltyRate] = useState(0.001); // 0.1%/day
  const [penaltyCap, setPenaltyCap] = useState(0.05);    // 5%
  const [commissionPreview, setCommissionPreview] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const addPhotos = (files) => {
    const imgs = [...files].filter((f) => f.type.startsWith("image/"));
    setPhotos((p) => [...p, ...imgs].slice(0, 6));
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/artisans");
        setArtisans(data.artisans || []);
        if (inviteFromQuery && (data.artisans || []).some((a) => a.id === inviteFromQuery)) {
          setInvited((p) => (p.includes(inviteFromQuery) ? p : [...p, inviteFromQuery]));
        }
      } catch {
        /* ignore */
      }
      try {
        const { data: cData } = await apiClient.get("/me/artisan-contacts");
        setContacts(cData.contacts || []);
      } catch {
        /* ignore */
      }
    })();
  }, [inviteFromQuery]);

  // Live commission preview (debounced)
  useEffect(() => {
    const amt = parseFloat(form.budget_max);
    if (!amt || amt <= 0) {
      setCommissionPreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/commission/preview?amount=${amt}`);
        setCommissionPreview(data);
      } catch {
        /* ignore */
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.budget_max]);

  const toggle = (id) =>
    setInvited((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const toggleEmailInvite = (id) =>
    setEmailInvites((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        budget_min: parseFloat(form.budget_min),
        budget_max: parseFloat(form.budget_max),
        surface_m2: form.surface_m2 ? parseFloat(form.surface_m2) : null,
        address: form.address.trim() || null,
        deadline: form.deadline ? format(form.deadline, "yyyy-MM-dd") : null,
        mo_fee_type: form.mo_fee_type || null,
        mo_fee_value: form.mo_fee_type && form.mo_fee_value ? parseFloat(form.mo_fee_value) : null,
        mo_fee_vat_franchise: !!form.mo_fee_vat_franchise,
        final_client_name: form.final_client_name.trim() || null,
        final_client_address: form.final_client_address.trim() || null,
        final_client_email: form.final_client_email.trim() || null,
        invited_artisans: invited,
        chantier_id: chantierIdFromQuery || null,
        late_penalty_enabled: penaltyEnabled && !!form.deadline,
        late_penalty_rate_per_day: penaltyRate,
        late_penalty_cap_percent: penaltyCap,
      };
      const { data } = await apiClient.post("/missions", payload);
      if (photos.length > 0) {
        toast.info(`Envoi de ${photos.length} photo(s)…`);
        for (const photo of photos) {
          try {
            const fd = new FormData();
            fd.append("stage", "brief");
            fd.append("caption", "Photo jointe à la demande");
            fd.append("file", photo);
            await apiClient.post(`/missions/${data.mission.id}/updates`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch {
            toast.error(`Échec de l'envoi de ${photo.name}`);
          }
        }
      }
      const selectedEmailContacts = contacts.filter((c) => !c.artisan_id && emailInvites.includes(c.id));
      for (const c of selectedEmailContacts) {
        try {
          await apiClient.post("/invitations", { role: "artisan", name: c.name, email: c.email, city: c.city, mission_id: data.mission.id });
        } catch {
          /* invitation déjà en attente ou email invalide — non bloquant */
        }
      }
      if (selectedEmailContacts.length > 0) {
        toast.info(`${selectedEmailContacts.length} invitation(s) envoyée(s) par email à vos artisans`);
      }
      toast.success("Mission publiée");
      nav(`/missions/${data.mission.id}`);
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// Nouvelle mission</div>
        <h1 className="display text-5xl mt-2 mb-10">Publier une demande</h1>
        {inviteFromQuery && invited.includes(inviteFromQuery) && (
          <div className="mb-8 border-2 border-[#FF4D00] bg-orange-50 p-4 flex items-center gap-3" data-testid="invite-prefilled-banner">
            <div className="w-9 h-9 bg-[#FF4D00] text-white flex items-center justify-center display text-lg shrink-0">
              {(artisans.find((a) => a.id === inviteFromQuery)?.name || "A")[0].toUpperCase()}
            </div>
            <div className="text-sm">
              <strong>{artisans.find((a) => a.id === inviteFromQuery)?.name || "Votre artisan"}</strong> sera automatiquement
              invité sur votre demande — il recevra votre chantier dès publication.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6" data-testid="new-mission-form">
          <div className="lg:col-span-2 border-2 border-[#0A0A0B] bg-white p-8 space-y-5">
            <Field
              label="Titre du chantier"
              value={form.title}
              onChange={(v) => setForm({ ...form, title: v })}
              required
              testid="mission-title"
              placeholder="Ex: Rénovation complète salle de bain"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Catégorie</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white"
                  data-testid="mission-category"
                >
                  {CATS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <Field
                label="Ville"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
                required
                testid="mission-city"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Surface concernée (m²)"
                type="number"
                value={form.surface_m2}
                onChange={(v) => setForm({ ...form, surface_m2: v })}
                testid="mission-surface"
                placeholder="Ex: 25"
              />
              <Field
                label="Adresse du projet (optionnel)"
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                testid="mission-address"
                placeholder="Ex: 12 rue des Lilas, 75011"
              />
            </div>
            <p className="text-[11px] text-zinc-500 -mt-2">
              L'adresse exacte n'est révélée qu'à l'artisan dont vous acceptez le devis — les autres ne voient que la ville.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Budget min (€)"
                type="number"
                value={form.budget_min}
                onChange={(v) => setForm({ ...form, budget_min: v })}
                required
                testid="mission-budget-min"
              />
              <Field
                label="Budget max (€)"
                type="number"
                value={form.budget_max}
                onChange={(v) => setForm({ ...form, budget_max: v })}
                required
                testid="mission-budget-max"
              />
            </div>
            <div>
              <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">
                Échéance souhaitée
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white text-left flex items-center justify-between hover:bg-zinc-50"
                    data-testid="mission-deadline"
                  >
                    <span className={form.deadline ? "text-[#0A0A0B]" : "text-zinc-400"}>
                      {form.deadline
                        ? format(form.deadline, "EEEE d MMMM yyyy", { locale: fr })
                        : "Sélectionner une date"}
                    </span>
                    <CalendarDays className="w-4 h-4 text-zinc-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="rounded-none border-2 border-[#0A0A0B] p-0" align="start">
                  <Calendar
                    mode="single"
                    locale={fr}
                    selected={form.deadline}
                    onSelect={(d) => setForm({ ...form, deadline: d })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Late penalty config */}
            <div className={`border-2 p-4 transition ${penaltyEnabled ? "border-[#FF4D00] bg-orange-50" : "border-zinc-300 bg-zinc-50"}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={penaltyEnabled}
                  onChange={(e) => setPenaltyEnabled(e.target.checked)}
                  disabled={!form.deadline}
                  className="w-4 h-4 mt-1 accent-[#FF4D00]"
                  data-testid="penalty-enable"
                />
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    Pénalités de retard (CCAG-Travaux)
                    {!form.deadline && <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">— échéance requise</span>}
                  </div>
                  <div className="text-xs text-zinc-700 mt-1">
                    En cas de livraison après l'échéance, déduction automatique sur le payout artisan, reversée au demandeur.
                  </div>
                </div>
              </label>
              {penaltyEnabled && form.deadline && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">% par jour de retard</label>
                    <input
                      type="number" step="0.001" min="0" max="0.05"
                      value={penaltyRate}
                      onChange={(e) => setPenaltyRate(parseFloat(e.target.value) || 0)}
                      className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white mono text-sm"
                      data-testid="penalty-rate"
                    />
                    <div className="mono text-[10px] text-zinc-500 mt-1">≈ {(penaltyRate * 100).toFixed(2)}% / jour</div>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">Plafond total</label>
                    <input
                      type="number" step="0.01" min="0" max="0.5"
                      value={penaltyCap}
                      onChange={(e) => setPenaltyCap(parseFloat(e.target.value) || 0)}
                      className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white mono text-sm"
                      data-testid="penalty-cap"
                    />
                    <div className="mono text-[10px] text-zinc-500 mt-1">Maximum {(penaltyCap * 100).toFixed(0)}% du montant</div>
                  </div>
                </div>
              )}
            </div>

            {/* Honoraires donneur d'ordre (MOE / gestionnaire sinistres) */}
            <div className={`border-2 p-4 transition ${form.mo_fee_type ? "border-[#FF4D00] bg-orange-50" : "border-zinc-300 bg-zinc-50"}`} data-testid="mo-fee-section">
              <div className="font-semibold">Vos honoraires de donneur d'ordre (optionnel)</div>
              <div className="text-xs text-zinc-700 mt-1">
                Maître d'œuvre, gestionnaire de sinistres, architecte : ajoutez vos honoraires — ils sont inclus dans le paiement séquestré et vous sont reversés à la validation du chantier. <strong>0% de commission Batisur sur vos honoraires.</strong>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">Type d'honoraires</label>
                  <select
                    value={form.mo_fee_type}
                    onChange={(e) => setForm({ ...form, mo_fee_type: e.target.value, mo_fee_value: "" })}
                    className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm"
                    data-testid="mo-fee-type"
                  >
                    <option value="">Aucun</option>
                    <option value="percent">% du montant du chantier</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                {form.mo_fee_type && (
                  <div>
                    <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">
                      {form.mo_fee_type === "percent" ? "Pourcentage (ex. 8)" : "Montant en €"}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step={form.mo_fee_type === "percent" ? "0.5" : "10"}
                      max={form.mo_fee_type === "percent" ? "50" : undefined}
                      value={form.mo_fee_value}
                      onChange={(e) => setForm({ ...form, mo_fee_value: e.target.value })}
                      className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white mono text-sm"
                      data-testid="mo-fee-value"
                    />
                  </div>
                )}
              </div>
              {form.mo_fee_type && (
                <label className="mt-3 flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.mo_fee_vat_franchise}
                    onChange={(e) => setForm({ ...form, mo_fee_vat_franchise: e.target.checked })}
                    className="accent-[#FF4D00]"
                    data-testid="mo-fee-vat-franchise"
                  />
                  Franchise en base de TVA (art. 293 B CGI) — ma facture d'honoraires sera émise sans TVA
                </label>
              )}
              {form.mo_fee_type && (
                <div className="mt-4 border-t-2 border-orange-200 pt-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-zinc-600">Client final (pour votre facture d'honoraires — jamais visible par les artisans)</div>
                  <div className="mt-2 grid sm:grid-cols-3 gap-3">
                    <input
                      placeholder="Nom (ex. Assurances Dupont)"
                      value={form.final_client_name}
                      onChange={(e) => setForm({ ...form, final_client_name: e.target.value })}
                      className="border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm"
                      data-testid="final-client-name"
                    />
                    <input
                      placeholder="Adresse"
                      value={form.final_client_address}
                      onChange={(e) => setForm({ ...form, final_client_address: e.target.value })}
                      className="border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm"
                      data-testid="final-client-address"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={form.final_client_email}
                      onChange={(e) => setForm({ ...form, final_client_email: e.target.value })}
                      className="border-2 border-[#0A0A0B] px-3 py-2 bg-white text-sm"
                      data-testid="final-client-email"
                    />
                  </div>
                  <p className="text-[11px] text-zinc-600 mt-2">
                    À la validation du chantier, votre facture d'honoraires Factur-X est générée automatiquement à son nom.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">Description détaillée</label>
              <textarea
                rows="6"
                required
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
                placeholder="Surface, matériaux, contraintes, état actuel, attentes…"
                data-testid="mission-description"
              />
            </div>
            <div>
              <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">
                Photos de l'existant (optionnel · max 6)
              </label>
              <label
                className="mt-2 flex items-center gap-3 border-2 border-dashed border-zinc-300 hover:border-[#FF4D00] p-4 cursor-pointer text-sm text-zinc-600"
                data-testid="mission-photos-drop"
              >
                <UserPlus className="hidden" />
                <span className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">+ Joindre des photos</span>
                <span className="text-xs text-zinc-500">Les artisans chiffrent plus juste quand ils voient l'état actuel.</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
                  data-testid="mission-photos-input"
                />
              </label>
              {photos.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="mission-photos-previews">
                  {photos.map((p, i) => (
                    <div key={`${p.name}-${i}`} className="relative border-2 border-[#0A0A0B]">
                      <img src={URL.createObjectURL(p)} alt={p.name} className="w-20 h-20 object-cover" />
                      <button
                        type="button"
                        onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute -top-2 -right-2 bg-[#0A0A0B] text-white w-5 h-5 text-xs leading-none"
                        data-testid={`mission-photo-remove-${i}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-batisur w-full disabled:opacity-60"
              data-testid="mission-submit"
            >
              {busy ? "Publication…" : "Publier la demande"}
            </button>
          </div>

          <aside className="border-2 border-[#0A0A0B] bg-white p-6">
            {commissionPreview && (
              <div className="mb-6 border-2 border-[#FF4D00] bg-orange-50 p-4" data-testid="commission-preview">
                <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Commission Batisur (paliers)</div>
                <div className="display text-3xl mt-2">{commissionPreview.per_side.toLocaleString("fr-FR")}€</div>
                <div className="text-xs text-zinc-700 mt-1">
                  par partie · taux effectif <strong>{commissionPreview.effective_rate_pct}%</strong>
                </div>
                <div className="text-xs text-zinc-600 mt-2">
                  Sur un budget de {parseFloat(form.budget_max).toLocaleString("fr-FR")}€, l'artisan touche net {(form.budget_max - commissionPreview.per_side).toLocaleString("fr-FR")}€.
                </div>
              </div>
            )}
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// Inviter</div>
            <div className="display text-2xl mt-2">Artisans (optionnel)</div>
            <p className="text-xs text-zinc-600 mt-2">
              Si vide, tous les artisans pourront répondre. Sinon, seuls les invités.
            </p>
            {contacts.length > 0 && (
              <div className="mt-5 border-2 border-[#FF4D00] bg-orange-50 p-3" data-testid="my-artisans-quick">
                <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Mes artisans (carnet)</div>
                <div className="mt-2 space-y-2">
                  {contacts.map((c) =>
                    c.artisan_id ? (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`my-artisan-${c.id}`}>
                        <input
                          type="checkbox"
                          checked={invited.includes(c.artisan_id)}
                          onChange={() => toggle(c.artisan_id)}
                          className="accent-[#FF4D00]"
                        />
                        <span className="font-semibold">{c.name}</span>
                        <span className="mono text-[9px] uppercase tracking-widest text-emerald-700">inscrit</span>
                      </label>
                    ) : (
                      <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`my-artisan-${c.id}`}>
                        <input
                          type="checkbox"
                          checked={emailInvites.includes(c.id)}
                          onChange={() => toggleEmailInvite(c.id)}
                          className="accent-[#FF4D00]"
                        />
                        <span className="font-semibold">{c.name}</span>
                        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">invitation email</span>
                      </label>
                    )
                  )}
                </div>
                <p className="text-[11px] text-zinc-600 mt-2">
                  Les non-inscrits recevront un email d'invitation lié à cette mission.
                </p>
              </div>
            )}
            <div className="mt-5 space-y-2 max-h-[440px] overflow-auto scrollbar-thin pr-2">
              {artisans.length === 0 && (
                <div className="border-2 border-dashed border-zinc-300 p-6 text-center">
                  <UserPlus className="w-7 h-7 mx-auto text-[#FF4D00]" />
                  <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 mt-3">
                    // Aucun artisan
                  </div>
                  <div className="text-sm text-zinc-600 mt-1">
                    Soyez les premiers : la mission sera visible par tout artisan inscrit.
                  </div>
                </div>
              )}
              {artisans.map((a) => (
                <label
                  key={a.id}
                  className="flex items-start gap-3 border-2 border-zinc-200 hover:border-[#0A0A0B] p-3 cursor-pointer"
                  data-testid={`invite-${a.id}`}
                >
                  <input
                    type="checkbox"
                    checked={invited.includes(a.id)}
                    onChange={() => toggle(a.id)}
                    className="mt-1 accent-[#FF4D00]"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{a.name}</div>
                    {a.company && <div className="text-xs text-zinc-500">{a.company}</div>}
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      {(a.specialties || []).join(" · ") || "Sans spécialité"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="mono text-[11px] uppercase tracking-widest text-zinc-600">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-2 border-2 border-[#0A0A0B] px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
        data-testid={testid}
      />
    </div>
  );
}
